import React, { useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { UtensilsCrossed, Eye, EyeOff } from 'lucide-react';
import { toast } from 'sonner';

export default function AuthPage() {
  const { login, register } = useAuth();
  const [isLogin, setIsLogin] = useState(true);
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      if (isLogin) {
        await login(email, password);
        toast.success('Welcome back!');
      } else {
        if (!name.trim()) { toast.error('Please enter your name'); setLoading(false); return; }
        await register(name, email, password);
        toast.success('Account created!');
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Something went wrong');
    }
    setLoading(false);
  };

  return (
    <div className="min-h-screen flex flex-col items-center justify-center px-4" style={{ background: '#F9F8F6' }}>
      <div className="w-full max-w-sm fade-in">
        {/* Logo Area */}
        <div className="text-center mb-10">
          <div className="inline-flex items-center justify-center w-16 h-16 rounded-2xl mb-4" style={{ background: '#005A8D' }}>
            <UtensilsCrossed className="w-8 h-8 text-white" />
          </div>
          <h1 className="text-3xl font-semibold tracking-tight" style={{ fontFamily: 'Outfit, sans-serif', color: '#1A1D21' }}>
            DadTab
          </h1>
          <p className="text-base mt-2" style={{ color: '#5E646E', fontFamily: 'DM Sans, sans-serif' }}>
            Chileno Bay Dining Tracker
          </p>
        </div>

        {/* Auth Form */}
        <div className="bg-white rounded-2xl p-6 shadow-sm border" style={{ borderColor: '#E8E6E0' }}>
          {/* Toggle */}
          <div className="flex rounded-xl p-1 mb-6" style={{ background: '#F0EFEA' }}>
            <button
              data-testid="auth-tab-login"
              onClick={() => setIsLogin(true)}
              className="flex-1 py-2.5 rounded-lg text-base font-medium transition-all"
              style={{
                background: isLogin ? '#FFFFFF' : 'transparent',
                color: isLogin ? '#1A1D21' : '#5E646E',
                boxShadow: isLogin ? '0 1px 3px rgba(0,0,0,0.08)' : 'none',
                fontFamily: 'DM Sans, sans-serif'
              }}
            >
              Sign In
            </button>
            <button
              data-testid="auth-tab-register"
              onClick={() => setIsLogin(false)}
              className="flex-1 py-2.5 rounded-lg text-base font-medium transition-all"
              style={{
                background: !isLogin ? '#FFFFFF' : 'transparent',
                color: !isLogin ? '#1A1D21' : '#5E646E',
                boxShadow: !isLogin ? '0 1px 3px rgba(0,0,0,0.08)' : 'none',
                fontFamily: 'DM Sans, sans-serif'
              }}
            >
              Create Account
            </button>
          </div>

          <form onSubmit={handleSubmit} className="space-y-4">
            {!isLogin && (
              <div className="fade-in">
                <Label className="text-base font-medium mb-2 block" style={{ color: '#1A1D21' }}>Name</Label>
                <Input
                  data-testid="auth-name-input"
                  className="h-14 text-lg rounded-xl"
                  style={{ background: '#F9F8F6', borderColor: '#E8E6E0' }}
                  placeholder="Your name"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                />
              </div>
            )}
            <div>
              <Label className="text-base font-medium mb-2 block" style={{ color: '#1A1D21' }}>Email</Label>
              <Input
                data-testid="auth-email-input"
                className="h-14 text-lg rounded-xl"
                style={{ background: '#F9F8F6', borderColor: '#E8E6E0' }}
                type="email"
                placeholder="you@email.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
              />
            </div>
            <div>
              <Label className="text-base font-medium mb-2 block" style={{ color: '#1A1D21' }}>Password</Label>
              <div className="relative">
                <Input
                  data-testid="auth-password-input"
                  className="h-14 text-lg rounded-xl pr-12"
                  style={{ background: '#F9F8F6', borderColor: '#E8E6E0' }}
                  type={showPassword ? 'text' : 'password'}
                  placeholder="Your password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                />
                <button
                  type="button"
                  data-testid="auth-toggle-password"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 p-1"
                  style={{ color: '#5E646E' }}
                >
                  {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                </button>
              </div>
            </div>

            <Button
              data-testid="auth-submit-button"
              type="submit"
              disabled={loading}
              className="w-full h-14 rounded-full text-lg font-medium mt-2 touch-active"
              style={{ background: '#005A8D', color: '#FFFFFF' }}
            >
              {loading ? 'Please wait...' : (isLogin ? 'Sign In' : 'Create Account')}
            </Button>
          </form>
        </div>
      </div>
    </div>
  );
}
